import { Component } from '@theme/component';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Custom store-to-store country selector (US / TW).
 *
 * @typedef {object} CountrySelectorRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLDivElement} panel
 * @property {HTMLElement} [triggerIcon]
 * @property {HTMLElement} [triggerLabel]
 *
 * @extends {Component<CountrySelectorRefs>}
 */
class WuuCountrySelector extends Component {
  requiredRefs = ['button', 'panel'];

  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Panel Display or Hidden
   *
   * @param {MouseEvent | KeyboardEvent} event
   */
  toggleSelector(event) {
    event?.stopPropagation();
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      const current = this.refs.panel.querySelector('[aria-current="true"]');
      current?.focus();
    });
  }

  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  /**
   * Display Reload Callback
   */
  connectedCallback() {
    super.connectedCallback();
    this.#handleTriggerCallback();
  }

  /**
   * Theme `on:click` listeners run on document in the capture phase, so
   * `event.currentTarget` is the document. Use `event.target` (proxied to the
   * element with the `on:click` attribute).
   *
   * @param {MouseEvent | KeyboardEvent} event
   */
  selectCountry(event) {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;

    const item = event.target;
    if (!(item instanceof HTMLElement)) return;

    const isCurrent = item.getAttribute('aria-current') === 'true';
    const url = (item.getAttribute('href') || item.dataset.url || '').trim();

    if (isCurrent || url === '' || url === '#') {
      event.preventDefault();
      this.hidePanel();
      return;
    }

    try {
      const dest = new URL(url, window.location.origin);
      dest.pathname = window.location.pathname;
      dest.search = window.location.search;
      dest.hash = '';
      event.preventDefault();
      window.location.assign(dest.href);
    } catch {
      // Fall through to the native <a href> navigation.
    }
  }

  #handleTriggerCallback() {
    const items = this.querySelectorAll('.wuu-country-selector__item');
    let current = this.querySelector('.wuu-country-selector__item[aria-current="true"]');

    if (!current && items[0]) {
      current = items[0];
      current.setAttribute('aria-current', 'true');
    }

    if (!current) return;

    const label = this.refs.triggerLabel || this.querySelector('.wuu-country-selector__trigger');
    const text =
      current.getAttribute('data-label') || current.querySelector('.wuu-country-selector__name')?.textContent?.trim() || '';
    if (label && text) {
      label.textContent = text;
      this.refs.button?.setAttribute('aria-label', text);
    }

    const icon = this.refs.triggerIcon || this.querySelector('.wuu-country-selector__trigger-icon');
    const sourceIcon = current.querySelector('.wuu-country-selector__icon');
    if (!(icon instanceof HTMLElement) || !sourceIcon) return;

    icon.innerHTML = sourceIcon.innerHTML;
    icon.removeAttribute('hidden');
    icon.setAttribute('aria-hidden', 'true');
  }

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.hidePanel();
    event.stopPropagation();
    this.refs.button.focus();
  };
}

if (!customElements.get('wuu-country-selector-component')) {
  customElements.define('wuu-country-selector-component', WuuCountrySelector);
}
